import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api, { clearAuth, hasStoredToken } from '../lib/api';

export default function ProtectedRoute({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const authCheckDone = useRef(false);
  
  // Function to refresh user data - can be called by child components
  const refreshUser = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      if (response.data?.user_id) {
        setUser(response.data);
        return response.data;
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
    return null;
  }, []);
  
  useEffect(() => {
    // If user data was passed via navigation state, use it immediately
    // This handles the redirect from AuthCallback
    if (location.state?.user) {
      setIsAuthenticated(true);
      setUser(location.state.user);
      // Clear the state to prevent stale data on refresh
      window.history.replaceState({}, document.title);
      return;
    }
    
    // Prevent duplicate auth checks
    if (authCheckDone.current) return;
    authCheckDone.current = true;
    
    const checkAuth = async () => {
      try {
        // Use the api instance which includes token from localStorage
        const response = await api.get('/auth/me');
        
        if (response.data?.user_id) {
          setIsAuthenticated(true);
          setUser(response.data);
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        // Auth check failed - clear token and mark as not authenticated
        clearAuth();
        setIsAuthenticated(false);
      }
    };
    
    checkAuth();
  }, [location.state]);
  
  useEffect(() => {
    if (isAuthenticated === false) {
      clearAuth();
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);
  
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#F87171] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#64748B]">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return null;
  }
  
  // Clone children and pass user prop along with refreshUser function
  return React.cloneElement(children, { user, refreshUser, setUser });
}
